import {Icon, Dialog, Button, ContextMenu, Li, Loading} from '@startlibs/components'
import {TextInput} from '@startlibs/form'
import {media, callIfFunction, formatDate} from '@startlibs/utils';
import {useToggle, usePopupToggle} from '@startlibs/core'
import React from 'react'
import _ from 'lodash/fp'
import styled from 'styled-components'

import {
  AttachmentActions,
  AttachmentBox,
  AttachmentDescription,
  AttachmentDetails,
  AttachmentIcon,
  AttachmentInfoContainer,
  DropdownButton,
  DropdownIcon,
  ProgressBar,
  ProgressDetails,
  TextButton,
  UploadedDate,
  UploadingStatus
} from './AttachmentBoxStyles';
import {getFileTypeIcon} from '../utils/FileType'
import {useUploader} from '../hooks/useUploader'
import {formatDateNoUTC} from '../utils'
import { useViewNonDicom } from '../utils/downloadFile';

export const SVS_DOCTYPE = 'Pathology Results'

const DOCUMENT_TYPES = [
  '',
  SVS_DOCTYPE,
  'Radiology',
  'Clinical Notes',
  'History and Physical',
  'Lab (other) Result',
  'Bloodwork Result',
  'Other'
]

const EditDetailButton = styled.div`
  display: inline-block;
  padding: 4px;
  border-radius: 5px;
  margin: -0.25rem 0 -0.25rem -4px;
  position: relative;
  ${media.max(520)`
    margin-top: -2px;
    margin-bottom: -4px;
  `}
  ${props => props.onClick && `:hover {
    background: rgba(0,0,0,0.06);
    cursor: pointer;
  }`}
  ${Icon} {
    font-size: 18px;
    margin-left: 0.25rem;
    vertical-align: -3px;
  }
  ${DropdownIcon} {
    font-size: 12px;
    vertical-align: -1px
  }
`
const canView = (attachment) => {
  return ["pdf","jpg","jpeg","png","gif","bmp","webp","pdf"].indexOf((attachment.fileExtension || "").toLowerCase())>=0
}

export const NonDicomBox = ({
                              jwt, appJwt, disabled, withoutDelete, attachment, onFinishUpload, onCancel, onFailure, removeFile, retry, setAttachment, isApp,
                              dragProvided, requestId
}) => {

  const params = {fileType: 'NON_DICOM'}

  const inQueue = attachment.sourceFile && !attachment.fileToUpload

  const descriptionToggle = useToggle()
  const docTypeToggle = usePopupToggle()
  const deleteDialog = useToggle()
  const loading = useToggle()
  const contextMenu = usePopupToggle()


  const confirmDescription = () => {
    setAttachment(_.set('description', descriptionToggle.isOpen))
    descriptionToggle.close()
  }

  const tryConfirmDescription = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      confirmDescription()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      descriptionToggle.close()
    }
  }

  const [openViewerLink,viewerIsLoading] = useViewNonDicom(appJwt, attachment, requestId)

  const [progress, cancel] = useUploader(attachment.fileToUpload, {
    params,
    jwt,
    onSuccess: onFinishUpload,
    onCancel: onCancel,
    onFailure: onFailure
  })

  return <AttachmentBox
    failedUpload={attachment.failed} isApp={isApp}
    ref={dragProvided.innerRef}
    {...dragProvided.draggableProps}
    {...dragProvided.dragHandleProps}
  >
    <AttachmentIcon icon={getFileTypeIcon(attachment.fileExtension)}/>
    <AttachmentInfoContainer>
      <AttachmentDescription><b className="nowrap">Description: </b>
        {
          _.isString(descriptionToggle.isOpen)
          ? <TextInput
            raw
            autoFocus
            onKeyUp={tryConfirmDescription}
            onKeyPress={e => e.key === "Enter" && e.preventDefault()}
            onBlur={confirmDescription}
            maxLength={255}
            value={descriptionToggle.isOpen}
            setValue={(v) => descriptionToggle.openWith(v)}
          />
          : <EditDetailButton onClick={disabled ? undefined : descriptionToggle.willOpenWith(attachment.description)}>
            {attachment.description}
            {!disabled && <Icon icon="edit-box"/>}
          </EditDetailButton>
        }</AttachmentDescription>
      <AttachmentDetails><b>Document type: </b>
        <EditDetailButton onClick={disabled ? undefined : docTypeToggle.open}>
          {attachment.docType || (disabled ? 'Other' : 'Select one')}
          {!disabled && <DropdownIcon icon="arrow-down"/>}
          {
            docTypeToggle.isOpen &&
            <ContextMenu>
              {DOCUMENT_TYPES.map(k => <Li label={k || 'Select one'} key={k}
                                           icon={attachment.docType === k ? 'check' : undefined}
                                           onClick={() => setAttachment(_.set('docType', k))}/>)}
            </ContextMenu>
          }
        </EditDetailButton>

      </AttachmentDetails>
    </AttachmentInfoContainer>
    {
      attachment.sourceFile
      ? (attachment.failed
         ? <UploadingStatus>
           <ProgressDetails failed>
             {
               attachment.failed === 'filename'
               ? <span>File name is not valid</span>
               : <span>File upload failed </span>
             }
             {
               attachment.failed === 'filename'
               ? <a className="link" onClick={cancel}>Cancel</a>
               : <a className="link" onClick={retry}>Retry</a>
             }
           </ProgressDetails>
         </UploadingStatus>
         : inQueue
           ? <UploadingStatus>
             <ProgressDetails>
               <span>Waiting...</span>
               <a className="link" onClick={cancel}>Cancel</a>
             </ProgressDetails>
             <ProgressBar progress={progress * 100} completed={progress * 100 === 100}/>
           </UploadingStatus>
           : <UploadingStatus>
             <ProgressDetails>
               <span>Uploading file...</span>
               <a className="link" onClick={cancel}>Cancel</a>
             </ProgressDetails>
             <ProgressBar progress={progress * 100} completed={progress * 100 === 100}/>
           </UploadingStatus>
      )
      : <AttachmentActions>
          <div className="buttons-wrapper">
            {
              !isApp &&
              <TextButton download={attachment.fileName + (attachment.fileExtension ? "." : "") + attachment.fileExtension}
                        href={`/api/viewNonDicom?file=${encodeURIComponent(attachment.uid + (attachment.fileExtension ? "." : "") + attachment.fileExtension)}&t=${callIfFunction(appJwt)}`}>
                <Icon icon="download"/>Download
              </TextButton>
            }
            {
              canView(attachment) && !isApp &&
              <TextButton
                // target="_blank"
                // href={`/api/viewNonDicom?file=${encodeURIComponent(attachment.uid + (attachment.fileExtension ? "." : "") + attachment.fileExtension)}&t=${callIfFunction(appJwt)}`}
                onClick={openViewerLink}
                isLoading={viewerIsLoading}
              >
                {viewerIsLoading ? <Loading size={15} borderWidth={3}/> : <Icon icon="view"/>}
                View
              </TextButton>
            }
            {
              !disabled && !withoutDelete && <DropdownButton onClick={contextMenu.open}>
              <DropdownIcon icon="arrow-down"/>
              {
                contextMenu.isOpen &&
                <ContextMenu>
                  <Li label="Delete medical record" icon="delete" onClick={deleteDialog.open}/>
                </ContextMenu>
              }
            </DropdownButton>
            }
          </div>
          {
            attachment.uploadDate && <UploadedDate>
              Uploaded: {formatDateNoUTC(new Date(attachment.uploadDate),"MM/dd/yyyy - hh:mm")}
            </UploadedDate>
          }
      </AttachmentActions>
    }
    {
      deleteDialog.isOpen &&
      <Dialog
        closeDialog={deleteDialog.close}
        title="Confirm medical record deletion"
        footer={<>
          <Button onClick={deleteDialog.close}>Cancel</Button>
          <Button alert isLoading={loading.isOpen} onClick={loading.willWait(removeFile)}>Delete medical record</Button>
        </>}
      >
        <p>You are about to delete the following medical record:</p>
        <AttachmentBox css="margin-bottom:1rem;">
          <AttachmentIcon icon={getFileTypeIcon(attachment.fileExtension)} className="desktop-only"/>
          <AttachmentInfoContainer>
            <AttachmentDescription><b className="nowrap">Description: </b>{attachment.description}</AttachmentDescription>
            <AttachmentDetails><b>Document type: </b>{attachment.docType || 'Other'}</AttachmentDetails>
          </AttachmentInfoContainer>
        </AttachmentBox>
        <p><b>Are you sure you want to delete it?</b> Once confirmed this cannot be undone.</p>
      </Dialog>
    }
  </AttachmentBox>
}
